import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Hash, Building2 } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useProjects } from "@/hooks/useProjects";
import { useLastOpenedProjects } from "@/hooks/useLastOpenedProjects";
import { formatProjectName } from "@/lib/format-project-name";
import { matchesSearchTokens } from "@/lib/search-utils";

export const PROJECT_SWITCHER_OPEN_EVENT = "project-switcher:open";

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

export function ProjectSwitcher() {
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();
  const { lastOpenedIds } = useLastOpenedProjects();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
    };

    window.addEventListener(PROJECT_SWITCHER_OPEN_EVENT, onOpen);
    return () =>
      window.removeEventListener(PROJECT_SWITCHER_OPEN_EVENT, onOpen);
  }, []);

  const recentProjects = useMemo(() => {
    const projectMap = new Map(
      projects.map((project) => [project.id, project]),
    );
    return lastOpenedIds
      .map((projectId) => projectMap.get(projectId))
      .filter(
        (project): project is (typeof projects)[number] =>
          project !== undefined,
      );
  }, [lastOpenedIds, projects]);

  const recentProjectIds = useMemo(
    () => new Set(recentProjects.map((project) => project.id)),
    [recentProjects],
  );

  const allProjects = useMemo(
    () => projects.filter((project) => !recentProjectIds.has(project.id)),
    [projects, recentProjectIds],
  );

  const normalizedQuery = query.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }

    return [...recentProjects, ...allProjects].filter((project) => {
      const displayName = formatProjectName(
        project.name,
        project.event_start_date,
      );
      const searchText = [
        project.name,
        displayName,
        project.number ?? "",
        project.companies?.name ?? "",
      ]
        .join(" ")
        .toLowerCase();

      return matchesSearchTokens(searchText, normalizedQuery);
    });
  }, [allProjects, normalizedQuery, recentProjects]);

  const openProject = (projectId: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/projects/${projectId}`);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const renderProjectItem = (
    project: (typeof projects)[number],
    shortcut?: ReactNode,
  ) => {
    const displayName = formatProjectName(
      project.name,
      project.event_start_date,
    );

    return (
      <CommandItem
        key={project.id}
        value={[
          project.name,
          displayName,
          project.number ?? "",
          project.companies?.name ?? "",
        ].join(" ")}
        onSelect={() => openProject(project.id)}
      >
        <FolderKanban className="mr-2 h-4 w-4" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{displayName}</span>
          <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            {project.number ? (
              <span className="flex min-w-0 items-center gap-1 truncate">
                <Hash className="h-3 w-3 shrink-0" />
                <span className="truncate">{project.number}</span>
              </span>
            ) : null}
            {project.companies?.name ? (
              <span className="flex min-w-0 items-center gap-1 truncate">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{project.companies.name}</span>
              </span>
            ) : null}
          </span>
        </div>
        {shortcut}
      </CommandItem>
    );
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      commandProps={{ shouldFilter: false }}
    >
      <CommandInput
        placeholder="Search projects by name, number, or company..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No projects found.</CommandEmpty>
        {normalizedQuery ? (
          <CommandGroup heading="Search Results">
            {searchResults.map((project, index) =>
              renderProjectItem(
                project,
                index === 0 && recentProjectIds.has(project.id) ? (
                  <CommandShortcut>Recent</CommandShortcut>
                ) : undefined,
              ),
            )}
          </CommandGroup>
        ) : null}
        {!normalizedQuery && recentProjects.length > 0 ? (
          <CommandGroup heading="Recent Projects">
            {recentProjects.map((project, index) =>
              renderProjectItem(
                project,
                index === 0 ? (
                  <CommandShortcut>Last Opened</CommandShortcut>
                ) : undefined,
              ),
            )}
          </CommandGroup>
        ) : null}
        {!normalizedQuery &&
        recentProjects.length > 0 &&
        allProjects.length > 0 ? (
          <CommandSeparator />
        ) : null}
        {!normalizedQuery && allProjects.length > 0 ? (
          <CommandGroup heading="All Projects">
            {allProjects.map((project) => renderProjectItem(project))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
